import React, { useState, useEffect, useContext } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useCookie from 'react-use-cookie';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';

import { useLogin } from 'hooks/useAuth';
import UserSessionContext from 'context/UserSessionContext';

import CheckboxButton from 'components/buttons/CheckboxButton';
import Container from 'components/Container';
import EmailInput from 'components/form-inputs/EmailInput';
import TitanLogoImage from 'assets/images/authentication/titan-logo.png';
import AuthHeaderImage from 'assets/images/authentication/auth-image.png';
import PasswordInput from './components/PasswordInput';

const useStyles = makeStyles(theme => ({
  heading: {
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: 0,
    marginTop: '14px'
  },
  displayFlex: {
    display: 'flex'
  },
  loginButton: {
    textTransform: 'none',
    padding: '10px 13px',
    fontSize: '14px'
  },
  link: {
    color: theme.palette.accent.main,
    textDecoration: 'none',
    display: 'block',
    fontWeight: '600',
    cursor: 'pointer'
  }
}));

const Login = () => {
  const classes = useStyles();
  const [loginState, login] = useLogin();
  const { isLoading, isError, error } = loginState;

  const { session } = useContext(UserSessionContext);
  const { isUserStateLoading, isLoggedIn, homepagePreference } = session;

  const { register, handleSubmit } = useForm();
  const [hasError, setHasError] = useState(false);

  const [isTermsChecked, setIsTermsChecked] = useCookie('terms-checked', false);

  const handleTermsCookie = () => {
    setIsTermsChecked(true, { days: 365 });
  };

  useEffect(() => {
    if (isError) {
      setHasError(isError);
    }
  }, [isError, loginState]);

  const handleFocus = () => {
    if (isError) {
      setHasError(false);
    }
  };

  if (!isUserStateLoading && isLoggedIn) {
    return <Redirect to={homepagePreference} />;
  }

  return (
    <Container size={100} center background="#346C9F">
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="center"
        style={{ paddingTop: '30px', paddingBottom: '30px' }}
      >
        <Grid item xs={12} md={4}>
          <Box
            component="img"
            src={TitanLogoImage}
            alt="alt"
            display="flex"
            maxWidth="100%"
            pb={2}
            style={{ margin: '0 auto' }}
          />

          <Box width="100%" bgcolor="#fff" borderRadius="4px">
            <Box component="img" src={AuthHeaderImage} alt="alt" display="flex" maxWidth="100%" />
            {hasError && !isLoading && (
              <Box bgcolor="red" color="#fff" p={2}>
                {error}
              </Box>
            )}
            <Box p={3} pt={1}>
              <form onSubmit={handleSubmit(login)}>
                <Typography variant="subtitle2" className={classes.heading}>
                  Username
                </Typography>
                <EmailInput
                  label="Type here..."
                  margin="dense"
                  inputRef={register}
                  disabled={isLoading}
                  onFocus={handleFocus}
                  type="text"
                  autoComplete="username"
                />
                <Typography variant="subtitle2" className={classes.heading}>
                  Password
                </Typography>
                <PasswordInput
                  label="Type here..."
                  margin="dense"
                  inputRef={register}
                  disabled={isLoading}
                  onFocus={handleFocus}
                />
                <Grid
                  item
                  container
                  direction="row"
                  alignItems="center"
                  justify="space-between"
                  style={{ marginTop: '20px' }}
                >
                  <Grid item xs={10}>
                    <CheckboxButton
                      title="I agree with the Terms & Conditions"
                      checked={isTermsChecked}
                      onTermsChange={handleTermsCookie}
                      required
                    />
                  </Grid>
                  <Grid item xs={2} className={classes.displayFlex}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      className={classes.loginButton}
                      type="submit"
                    >
                      Login
                    </Button>
                  </Grid>
                </Grid>
                <Box py={3}>
                  <Divider />
                </Box>
                <Typography
                  component={Link}
                  to="/terms-and-conditions"
                  variant="subtitle2"
                  align="center"
                  className={classes.link}
                  gutterBottom
                  style={{ marginBottom: '10px' }}
                >
                  Terms and Conditions
                </Typography>
                <Typography
                  component={Link}
                  to="/forgot-password"
                  variant="subtitle2"
                  align="center"
                  className={classes.link}
                >
                  Forgot your Password?
                </Typography>
              </form>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Login;
